import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Inbox, MapPinned, ArrowRight, Loader2, Bell, HandCoins } from "lucide-react";

const fmtDateTime = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
};

/**
 * Homepage widget showing two streams from employees:
 * - documents uploaded by employees (employee-uploads)
 * - data change notifications (employee-changes)
 */
export const EmployeeNotificationsWidget = () => {
    const [uploads, setUploads] = useState({ new_count: 0, items: [] });
    const [changes, setChanges] = useState({ new_count: 0, items: [] });
    const [advReqs, setAdvReqs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        Promise.allSettled([
            api.get("/employee-uploads", { params: { status: "new" } }),
            api.get("/employee-changes", { params: { status: "new" } }),
            api.get("/advance-requests", { params: { status: "pending" } }),
        ]).then((results) => {
            if (!mounted) return;
            if (results[0].status === "fulfilled") setUploads(results[0].value.data);
            if (results[1].status === "fulfilled") setChanges(results[1].value.data);
            if (results[2].status === "fulfilled") setAdvReqs(results[2].value.data || []);
        }).finally(() => mounted && setLoading(false));
        return () => { mounted = false; };
    }, []);

    if (loading) {
        return (
            <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm" data-testid="employee-notifications-widget">
                <div className="flex h-32 items-center justify-center">
                    <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                </div>
            </section>
        );
    }

    const totalNew = (uploads.new_count || 0) + (changes.new_count || 0) + advReqs.length;
    const topUploads = (uploads.items || []).slice(0, 3);
    const topChanges = (changes.items || []).slice(0, 3);

    if (totalNew === 0) {
        return (
            <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm" data-testid="employee-notifications-widget">
                <h2 className="flex items-center gap-2 font-display text-base font-semibold text-slate-700">
                    <Bell className="h-4 w-4 text-[#376DB3]" />
                    Notifiche dai dipendenti
                </h2>
                <p className="mt-3 text-sm text-slate-500">Nessuna nuova segnalazione.</p>
            </section>
        );
    }

    return (
        <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm" data-testid="employee-notifications-widget">
            <div className="mb-3 flex items-center justify-between">
                <h2 className="flex items-center gap-2 font-display text-base font-semibold text-slate-700">
                    <Bell className="h-4 w-4 text-[#376DB3]" />
                    Notifiche dai dipendenti
                    <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-bold text-white" data-testid="notif-total-badge">
                        {totalNew}
                    </span>
                </h2>
            </div>

            {/* Uploads */}
            {(uploads.new_count || 0) > 0 && (
                <div className="mb-3" data-testid="notif-uploads-block">
                    <Link
                        to="/documenti/dipendenti"
                        className="flex items-center justify-between rounded-md border border-[#376DB3]/15 bg-[#376DB3]/5 px-3 py-2 text-sm hover:bg-[#376DB3]/10"
                    >
                        <div className="flex items-center gap-2">
                            <Inbox className="h-4 w-4 text-[#376DB3]" />
                            <span className="font-medium text-[#376DB3]">
                                {uploads.new_count} document{uploads.new_count === 1 ? "o" : "i"} ricevut{uploads.new_count === 1 ? "o" : "i"}
                            </span>
                        </div>
                        <ArrowRight className="h-3.5 w-3.5 text-[#376DB3]" />
                    </Link>
                    <ul className="mt-1 ml-6 list-disc text-xs text-slate-600">
                        {topUploads.map((u) => (
                            <li key={u.id} className="truncate" title={u.title}>
                                <b>{u.employee_label || "—"}</b>: {u.title}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Advance requests */}
            {advReqs.length > 0 && (
                <div className="mb-3" data-testid="notif-advreqs-block">
                    <Link
                        to="/acconti"
                        className="flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm hover:bg-emerald-100"
                    >
                        <div className="flex items-center gap-2">
                            <HandCoins className="h-4 w-4 text-emerald-600" />
                            <span className="font-medium text-emerald-900">
                                {advReqs.length} richiest{advReqs.length === 1 ? "a" : "e"} di acconto da approvare
                            </span>
                        </div>
                        <ArrowRight className="h-3.5 w-3.5 text-emerald-700" />
                    </Link>
                    <ul className="mt-1 ml-6 list-disc text-xs text-slate-600">
                        {advReqs.slice(0, 3).map((r) => (
                            <li key={r.id} className="truncate" title={r.reason}>
                                <b>{r.employee_label || "—"}</b>: € {Number(r.amount).toFixed(2)}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Data changes */}
            {(changes.new_count || 0) > 0 && (
                <div data-testid="notif-changes-block">
                    <Link
                        to="/notifiche/modifiche-dati"
                        className="flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm hover:bg-amber-100"
                    >
                        <div className="flex items-center gap-2">
                            <MapPinned className="h-4 w-4 text-amber-600" />
                            <span className="font-medium text-amber-900">
                                {changes.new_count} modific{changes.new_count === 1 ? "a" : "he"} dati personali
                            </span>
                        </div>
                        <ArrowRight className="h-3.5 w-3.5 text-amber-700" />
                    </Link>
                    <ul className="mt-1 ml-6 list-disc text-xs text-slate-600">
                        {topChanges.map((c) => (
                            <li key={c.id} className="truncate">
                                <Link to={`/dipendenti/${c.employee_id}`} className="font-medium text-[#376DB3] hover:underline">
                                    {c.employee_label || "—"}
                                </Link>
                                {" — "}
                                {c.residence_changed ? <b className="text-amber-700">Cambio residenza</b> : "Cambio contatti"}
                                <span className="ml-1 text-slate-400">({fmtDateTime(c.created_at)})</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </section>
    );
};
